import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import {
  ShieldCheck,
  ShieldAlert,
  ShieldOff,
  Users,
  Smartphone,
  Mail,
  Key,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  RotateCcw,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Download
} from 'lucide-react'
import IdentityVerificationModal from '../components/IdentityVerificationModal'

// Badge per stato 2FA
const TwoFactorBadge = ({ enabled, methods }) => {
  if (!enabled) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
        <ShieldOff className="h-3 w-3 mr-1" />
        Non attivo
      </span>
    )
  }

  const methodIcons = []
  if (methods?.totp) methodIcons.push(<Smartphone key="totp" className="h-3 w-3" title="TOTP" />)
  if (methods?.emailOtp) methodIcons.push(<Mail key="email" className="h-3 w-3" title="Email OTP" />)
  if (methods?.webauthn) methodIcons.push(<Key key="webauthn" className="h-3 w-3" title="Passkey" />)

  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <ShieldCheck className="h-3 w-3" />
      Attivo
      {methodIcons.length > 0 && (
        <span className="flex items-center gap-0.5 ml-1 pl-1 border-l border-green-300">
          {methodIcons}
        </span>
      )}
    </span>
  )
}

// Card statistiche
const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
        <p className="mt-1 text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
        {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
)

// Badge azione audit con colori appropriati
const ActionBadge = ({ action, actionLabel }) => {
  const getStyle = () => {
    if (action?.includes('enabled')) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
    if (action?.includes('disabled')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
    if (action?.includes('reset')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
    if (action?.includes('webauthn') || action?.includes('passkey')) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    if (action?.includes('totp')) return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    if (action?.includes('backup')) return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    if (action?.includes('trusted')) return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400'
    if (action?.includes('verification')) return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }

  const getIcon = () => {
    if (action?.includes('enabled')) return '✓'
    if (action?.includes('disabled')) return '✗'
    if (action?.includes('reset')) return '↻'
    if (action?.includes('webauthn') || action?.includes('passkey')) return '🔑'
    if (action?.includes('totp')) return '📱'
    if (action?.includes('backup')) return '🎫'
    if (action?.includes('trusted')) return '💻'
    if (action?.includes('verification_success')) return '✅'
    if (action?.includes('verification_failed')) return '❌'
    return '•'
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStyle()}`}>
      <span className="mr-1">{getIcon()}</span>
      {actionLabel || action?.replace('2fa_', '').replace(/_/g, ' ')}
    </span>
  )
}

export default function TwoFactorPage() {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [auditLogs, setAuditLogs] = useState([])
  const [error, setError] = useState(null)
  
  // Filtri utenti
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [filter2FA, setFilter2FA] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  
  // Modali
  const [selectedUser, setSelectedUser] = useState(null)
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const [resetReason, setResetReason] = useState('')
  const [resetting, setResetting] = useState(false)

  // State per modale verifica identità
  const [verificationModalOpen, setVerificationModalOpen] = useState(false)
  const [verificationUser, setVerificationUser] = useState(null)

  // Tab attiva
  const [activeTab, setActiveTab] = useState('users') // 'users' | 'audit'

  // ========== AUDIT LOG STATE ==========
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditPagination, setAuditPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0
  })
  const [auditFilters, setAuditFilters] = useState({
    startDate: '',
    endDate: '',
    action: '',
    searchUser: '',
    tenantId: ''
  })
  const [availableActions, setAvailableActions] = useState([])
  const [expandedRows, setExpandedRows] = useState({})

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Carica statistiche 2FA globali
      const [statsRes, usersRes] = await Promise.all([
        adminApi.get2FAStats(),
        adminApi.get2FAUsers()
      ])

      // Mappa la risposta del backend alla struttura attesa dal frontend
      const rawStats = statsRes.data?.data
      if (rawStats) {
        setStats({
          total: rawStats.overview?.totalUsers || 0,
          with2FA: rawStats.overview?.usersWithTwoFactor || 0,
          without2FA: rawStats.overview?.usersWithoutTwoFactor || 0,
          percentage: rawStats.overview?.adoptionRate || 0,
          byMethod: rawStats.byMethod || {},
          byRole: rawStats.byRole || {},
          trustedDevices: rawStats.trustedDevices || 0
        })
      } else {
        setStats(null)
      }
      
      // Mappa utenti - la risposta contiene info 2FA nested in twoFactor
      const rawUsers = usersRes.data?.data?.users || []
      setUsers(rawUsers.map(u => ({
        id: u.id,
        email: u.email,
        nome: u.nome,
        cognome: u.cognome,
        role: u.role,
        tenant: u.tenant,
        lastLogin: u.lastLogin,
        twoFactorEnabled: u.twoFactorEnabled || u.twoFactor?.enabled || false,
        methods: u.methods || {
          totp: u.twoFactor?.methods?.totp || false,
          emailOtp: u.twoFactor?.methods?.emailOtp || false,
          webauthn: u.twoFactor?.methods?.webauthn || false
        },
        backupCodesRemaining: u.twoFactor?.backupCodesRemaining || 0,
        trustedDevicesCount: u.twoFactor?.trustedDevicesCount || 0
      })))

    } catch (err) {
      console.error('Load 2FA data error:', err)
      setError(err.response?.data?.message || 'Errore nel caricamento dei dati 2FA')
    } finally {
      setLoading(false)
    }
  }

  // ========== AUDIT LOG FUNCTIONS ==========
  const loadAuditLogs = async (page = 1) => {
    try {
      setAuditLoading(true)
      
      const params = {
        page,
        limit: auditPagination.limit,
        ...auditFilters
      }
      
      // Rimuovi parametri vuoti
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key]
      })

      const res = await adminApi.get2FAAudit(params)
      const data = res.data?.data
      
      setAuditLogs(data?.logs || [])
      setAuditPagination({
        page: data?.pagination?.page || 1,
        limit: data?.pagination?.limit || 25,
        total: data?.pagination?.total || 0,
        pages: data?.pagination?.pages || 0
      })
      
      // Salva azioni disponibili per il filtro
      if (data?.filters?.availableActions) {
        setAvailableActions(data.filters.availableActions)
      }
      
    } catch (err) {
      console.error('Load audit logs error:', err)
    } finally {
      setAuditLoading(false)
    }
  }

  // Carica audit quando si passa al tab
  useEffect(() => {
    if (activeTab === 'audit') {
      loadAuditLogs(1)
    }
  }, [activeTab])

  // Applica filtri
  const handleApplyAuditFilters = () => {
    setExpandedRows({})
    loadAuditLogs(1)
  }

  // Reset filtri
  const handleResetAuditFilters = () => {
    setAuditFilters({
      startDate: '',
      endDate: '',
      action: '',
      searchUser: '',
      tenantId: ''
    })
    setExpandedRows({})
    // Ricarica con filtri vuoti
    setTimeout(() => loadAuditLogs(1), 0)
  }

  // Cambia pagina
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= auditPagination.pages) {
      setExpandedRows({})
      loadAuditLogs(newPage)
    }
  }

  // Toggle riga espansa
  const toggleRowExpanded = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }))
  }

  // Export CSV (base)
  const handleExportCSV = () => {
    if (auditLogs.length === 0) return
    
    const headers = ['Data', 'Utente', 'Email', 'Centro', 'Azione', 'IP', 'Dettagli']
    const rows = auditLogs.map(log => [
      log.createdAt ? new Date(log.createdAt).toLocaleString('it-IT') : '',
      log.user ? `${log.user.nome} ${log.user.cognome}` : '',
      log.user?.email || log.email || '',
      log.tenant?.name || '',
      log.actionLabel || log.action,
      log.ipAddress || '',
      log.metadata?.reason || ''
    ])
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `audit-2fa-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  // Filtro utenti
  const filteredUsers = users.filter(user => {
    const matchSearch = !searchTerm || 
      user.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.cognome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchRole = !filterRole || user.role === filterRole
    
    const match2FA = !filter2FA || 
      (filter2FA === 'enabled' && user.twoFactorEnabled) ||
      (filter2FA === 'disabled' && !user.twoFactorEnabled)

    return matchSearch && matchRole && match2FA
  })

  // Gestisce click su "Reset 2FA" - verifica se serve verifica identità
  const handleResetClick = async (user) => {
    // Se è un coordinatore, verifica se è l'unico del suo centro
    if (user.role === 'coordinatore') {
      try {
        // Carica dettagli utente per verificare se è l'unico coordinatore
        const response = await adminApi.get2FAUser(user.id)
        const userData = response.data?.data
        
        if (userData?.user?.isOnlyCoordinator) {
          // È l'unico coordinatore - apri modale verifica identità
          setVerificationUser({
            id: user.id,
            nome: user.nome,
            cognome: user.cognome,
            email: user.email,
            role: user.role
          })
          setVerificationModalOpen(true)
          return
        }
      } catch (err) {
        console.error('Error checking coordinator status:', err)
        // In caso di errore, procedi con reset normale
      }
    }
    
    // Per altri casi (educatori o coordinatori non unici), usa il reset normale
    setSelectedUser(user)
    setResetModalOpen(true)
  }

  // Callback quando verifica identità completata con successo
  const handleVerificationSuccess = () => {
    loadData() // Ricarica lista utenti
    setVerificationModalOpen(false)
    setVerificationUser(null)
  }

  // Reset 2FA
  const handleResetRequest = async () => {
    if (!selectedUser) return

    try {
      setResetting(true)
      await adminApi.reset2FA(selectedUser.id, { reason: resetReason })
      
      // Ricarica dati
      await loadData()
      
      // Chiudi modale
      setResetModalOpen(false)
      setSelectedUser(null)
      setResetReason('')

    } catch (err) {
      console.error('Reset 2FA error:', err)
      alert(err.response?.data?.message || 'Errore nel reset 2FA')
    } finally {
      setResetting(false)
    }
  }

  // Force reset (solo superadmin)
  const handleForceReset = async (userId) => {
    if (!confirm('Sei sicuro di voler forzare il reset 2FA? L\'utente dovrà riconfigurare la 2FA.')) {
      return
    }

    try {
      await adminApi.forceReset2FA(userId, { reason: 'Force reset da admin panel' })
      await loadData()
    } catch (err) {
      console.error('Force reset error:', err)
      alert(err.response?.data?.message || 'Errore nel force reset')
    }
  }

  // Formatta user agent per visualizzazione breve
  const formatUserAgent = (ua) => {
    if (!ua) return '-'
    // Estrai browser e OS
    const browser = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)?.[0] || ''
    const os = ua.match(/(Windows|Mac OS X|Linux|Android|iOS)[\s\d._]*/)?.[0] || ''
    return `${browser} / ${os}`.trim() || ua.substring(0, 50) + '...'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
        <button 
          onClick={loadData}
          className="mt-2 text-sm text-red-600 hover:text-red-700"
        >
          Riprova
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestione 2FA</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Monitora e gestisci l'autenticazione a due fattori degli utenti
          </p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Utenti Totali"
            value={stats.total}
            icon={Users}
            color="bg-blue-500"
          />
          <StatCard
            title="Con 2FA Attivo"
            value={stats.with2FA}
            icon={ShieldCheck}
            color="bg-green-500"
            subtitle={`${stats.percentage}% di adozione`}
          />
          <StatCard
            title="Senza 2FA"
            value={stats.without2FA}
            icon={ShieldAlert}
            color="bg-yellow-500"
          />
          <StatCard
            title="Dispositivi Fidati"
            value={stats.trustedDevices || 0}
            icon={Smartphone}
            color="bg-purple-500"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Users className="h-4 w-4 inline mr-2" />
            Utenti ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'audit'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            <Eye className="h-4 w-4 inline mr-2" />
            Audit Log
          </button>
        </nav>
      </div>

      {/* Tab Content: Users */}
      {activeTab === 'users' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Cerca per nome o email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Filters */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center px-3 py-2 text-sm border rounded-lg ${
                    showFilters || filterRole || filter2FA
                      ? 'border-primary-500 text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Filtri
                  {(filterRole || filter2FA) && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-500 text-white rounded-full">
                      {(filterRole ? 1 : 0) + (filter2FA ? 1 : 0)}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Filter options */}
            {showFilters && (
              <div className="mt-4 flex flex-wrap gap-4">
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  <option value="">Tutti i ruoli</option>
                  <option value="coordinatore">Coordinatore</option>
                  <option value="educatore">Educatore</option>
                </select>

                <select
                  value={filter2FA}
                  onChange={(e) => setFilter2FA(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                >
                  <option value="">Stato 2FA</option>
                  <option value="enabled">2FA Attivo</option>
                  <option value="disabled">2FA Non attivo</option>
                </select>

                {(filterRole || filter2FA) && (
                  <button
                    onClick={() => {
                      setFilterRole('')
                      setFilter2FA('')
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Resetta filtri
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Utente
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ruolo
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Stato 2FA
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ultimo accesso
                  </th>
                  <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                      Nessun utente trovato
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 md:px-6 py-4">
                        <div className="min-w-0 max-w-[180px] sm:max-w-none">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {user.nome} {user.cognome}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {user.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'coordinatore'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                        <TwoFactorBadge enabled={user.twoFactorEnabled} methods={user.methods} />
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : 'Mai'
                        }
                      </td>
                      <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          {user.twoFactorEnabled && (
                            <>
                              <button
                                onClick={() => handleResetClick(user)}
                                className="text-yellow-600 hover:text-yellow-700 p-1"
                                title="Richiedi reset 2FA"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleForceReset(user.id)}
                                className="text-red-600 hover:text-red-700 p-1"
                                title="Force reset 2FA"
                              >
                                <ShieldOff className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="text-gray-400 hover:text-gray-600 p-1"
                            title="Dettagli"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab Content: Audit */}
      {activeTab === 'audit' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-white">Eventi 2FA</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCSV}
                disabled={auditLogs.length === 0}
                className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400 flex items-center px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4 mr-1" />
                Esporta CSV
              </button>
              <button
                onClick={() => loadAuditLogs(auditPagination.page)}
                disabled={auditLoading}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center px-3 py-1.5 border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${auditLoading ? 'animate-spin' : ''}`} />
                Aggiorna
              </button>
            </div>
          </div>

          {/* Filtri */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Date picker: Da */}
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  Da data
                </label>
                <input
                  type="date"
                  value={auditFilters.startDate}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Date picker: A */}
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  A data
                </label>
                <input
                  type="date"
                  value={auditFilters.endDate}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Filtro Azione */}
              <div className="flex-1 min-w-[160px]">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Azione
                </label>
                <select
                  value={auditFilters.action}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, action: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Tutte le azioni</option>
                  {availableActions.map(a => (
                    <option key={a.value} value={a.value}>{a.label}</option>
                  ))}
                </select>
              </div>

              {/* Filtro Utente */}
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  <Search className="h-3 w-3 inline mr-1" />
                  Utente
                </label>
                <input
                  type="text"
                  value={auditFilters.searchUser}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, searchUser: e.target.value }))}
                  placeholder="Cerca per nome o email..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Pulsanti */}
              <div className="flex gap-2">
                <button
                  onClick={handleApplyAuditFilters}
                  disabled={auditLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  Applica
                </button>
                <button
                  onClick={handleResetAuditFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Tabella */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="w-8 px-2 md:px-4 py-3"></th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Data
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Utente
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Centro
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Azione
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    IP
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {auditLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-600 mx-auto" />
                    </td>
                  </tr>
                ) : auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                      Nessun evento registrato
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <>
                      {/* Riga principale */}
                      <tr 
                        key={log.id}
                        onClick={() => toggleRowExpanded(log.id)}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      >
                        <td className="px-2 md:px-4 py-4 text-gray-400">
                          <ChevronRight className={`h-4 w-4 transform transition-transform ${expandedRows[log.id] ? 'rotate-90' : ''}`} />
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                          {log.createdAt
                            ? new Date(log.createdAt).toLocaleString('it-IT', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })
                            : '-'
                          }
                        </td>
                        <td className="px-3 md:px-6 py-4">
                          <div className="min-w-0 max-w-[160px] sm:max-w-none">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {log.user?.nome} {log.user?.cognome}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {log.user?.email || log.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 md:px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="block max-w-[120px] sm:max-w-none truncate">{log.tenant?.name || '-'}</span>
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                          <ActionBadge action={log.action} actionLabel={log.actionLabel} />
                        </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.ipAddress || '-'}
                        </td>
                      </tr>

                      {/* Riga dettagli espandibile */}
                      {expandedRows[log.id] && (
                        <tr className="bg-blue-50 dark:bg-blue-900/20">
                          <td colSpan={6} className="px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Ruolo:</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-white capitalize">
                                  {log.user?.role || '-'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">Browser:</span>
                                <span className="ml-2 font-medium text-gray-900 dark:text-white text-xs">
                                  {formatUserAgent(log.userAgent)}
                                </span>
                              </div>
                              {log.metadata?.reason && (
                                <div className="col-span-2">
                                  <span className="text-gray-500 dark:text-gray-400">Motivo:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {log.metadata.reason}
                                  </span>
                                </div>
                              )}
                              {log.metadata?.method && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Metodo:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white uppercase">
                                    {log.metadata.method}
                                  </span>
                                </div>
                              )}
                              {log.metadata?.deviceName && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Dispositivo:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {log.metadata.deviceName}
                                  </span>
                                </div>
                              )}
                              {log.metadata?.backupCodesRemaining !== undefined && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Backup rimanenti:</span>
                                  <span className={`ml-2 font-medium ${log.metadata.backupCodesRemaining <= 3 ? 'text-orange-600' : 'text-gray-900 dark:text-white'}`}>
                                    {log.metadata.backupCodesRemaining <= 3 ? '⚠️ ' : ''}{log.metadata.backupCodesRemaining}
                                  </span>
                                </div>
                              )}
                              {log.metadata?.performedBy && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Eseguito da:</span>
                                  <span className="ml-2 font-medium text-gray-900 dark:text-white">
                                    {log.metadata.performedBy}
                                  </span>
                                </div>
                              )}
                              {log.metadata?.reportId && (
                                <div>
                                  <span className="text-gray-500 dark:text-gray-400">Report:</span>
                                  <span className="ml-2 font-mono text-xs text-blue-600 dark:text-blue-400">
                                    {log.metadata.reportId}
                                  </span>
                                </div>
                              )}
                              <div>
                                <span className="text-gray-500 dark:text-gray-400">ID Evento:</span>
                                <span className="ml-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                                  {log.id}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginazione */}
          {auditPagination.pages > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Mostrando{' '}
                <span className="font-medium">
                  {((auditPagination.page - 1) * auditPagination.limit) + 1}-
                  {Math.min(auditPagination.page * auditPagination.limit, auditPagination.total)}
                </span>{' '}
                di <span className="font-medium">{auditPagination.total}</span> eventi
              </div>

              <div className="flex items-center gap-2">
                {/* Righe per pagina */}
                <select
                  value={auditPagination.limit}
                  onChange={(e) => {
                    setAuditPagination(prev => ({ ...prev, limit: parseInt(e.target.value) }))
                    setTimeout(() => loadAuditLogs(1), 0)
                  }}
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-700"
                >
                  <option value={10}>10 per pagina</option>
                  <option value={25}>25 per pagina</option>
                  <option value={50}>50 per pagina</option>
                  <option value={100}>100 per pagina</option>
                </select>

                {/* Navigazione */}
                <nav className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(auditPagination.page - 1)}
                    disabled={auditPagination.page <= 1}
                    className="px-3 py-1 text-sm text-gray-500 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Prec
                  </button>

                  {/* Numeri pagina */}
                  {Array.from({ length: Math.min(5, auditPagination.pages) }, (_, i) => {
                    let pageNum
                    if (auditPagination.pages <= 5) {
                      pageNum = i + 1
                    } else if (auditPagination.page <= 3) {
                      pageNum = i + 1
                    } else if (auditPagination.page >= auditPagination.pages - 2) {
                      pageNum = auditPagination.pages - 4 + i
                    } else {
                      pageNum = auditPagination.page - 2 + i
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-lg ${
                          pageNum === auditPagination.page
                            ? 'text-white bg-primary-600 border-primary-600'
                            : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}

                  <button
                    onClick={() => handlePageChange(auditPagination.page + 1)}
                    disabled={auditPagination.page >= auditPagination.pages}
                    className="px-3 py-1 text-sm text-gray-500 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    Succ
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </nav>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Reset 2FA */}
      {resetModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Richiedi Reset 2FA
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Stai per inviare una richiesta di reset 2FA a{' '}
              <strong>{selectedUser.nome} {selectedUser.cognome}</strong>.
              L'utente riceverà un'email per confermare il reset.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Motivo (opzionale)
              </label>
              <textarea
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={3}
                placeholder="Es: L'utente ha perso l'accesso al telefono"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setResetModalOpen(false)
                  setSelectedUser(null)
                  setResetReason('')
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Annulla
              </button>
              <button
                onClick={handleResetRequest}
                disabled={resetting}
                className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center"
              >
                {resetting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Invio...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Invia richiesta
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Verifica Identità per Coordinatori Unici */}
      <IdentityVerificationModal
        isOpen={verificationModalOpen}
        onClose={() => {
          setVerificationModalOpen(false)
          setVerificationUser(null)
        }}
        user={verificationUser}
        onSuccess={handleVerificationSuccess}
      />
    </div>
  )
}
